/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';
import type { SerializedStyles } from '@emotion/react';

import * as React from 'react';

import {
  Image,
  MODULE_LAYOUTS,
  ModuleLayout,
  getModuleLayoutConfig,
  isHorizontalLayout,
  isInvertedColor,
} from '@coursera-web/cds-labs';
import type { GridConfig, ImageType, ModuleLayoutConfig } from '@coursera-web/cds-labs';

import { Button, Chip, ChipGroup, Hidden, Skeleton, Typography2, breakpoints, useMediaQuery } from '@coursera/cds-core';
import type { ButtonProps, Button as ButtonType } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';

import type { CarouselCollection } from 'bundles/collections-carousel/types/CarouselCollection';
import CollectionsContainer from 'bundles/collections-common/components/CollectionsContainer';
import withSingleTracked from 'bundles/common/components/withSingleTracked';
import { MAX_NUMBER_OF_SPOTLIGHT_COLLECTION_PRODUCTS } from 'bundles/search-common/constants';
import { getShouldShowGoogleAISpotlightCollectionsInSearch } from 'bundles/search-common/utils/experimentUtils';

import _t from 'i18n!nls/search-common';

const TrackedChip = withSingleTracked({ type: 'BUTTON' })(Chip);

export type CTA = {
  text?: string | null;
  target?: string | null;
  variant?: ButtonProps['variant'] | null;
  showCtaArrowIcon?: boolean;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export type Filter = {
  id: string;
  filterName: string;
  filterImage?: ImageType | null;
};

export type Props = {
  headline?: string | null;
  description?: string | null;
  showAllEntries?: boolean | null;
  filters?: Filter[] | null;
  collections?: CarouselCollection[];
  isCollectionsLoading?: boolean;
  onFilterChange?: (index: number, filterName: string) => void;
  currentCollectionSetIndex?: number;
  numOfEntriesPerCollection?: number;
  cta?: CTA;
  customCtaButton?: React.ReactNode;
  customChipStyles?: SerializedStyles;
  forceOpenInNewTab?: boolean;
  productCardTrackingName?: string | null;
  eventingData?: Record<string, any>;
} & ModuleLayoutConfig;

const styles = {
  heading: css`
    margin-bottom: var(--cds-spacing-200) !important;
  `,

  description: css`
    margin-bottom: var(--cds-spacing-200);
  `,

  filters: css`
    margin-bottom: var(--cds-spacing-100);

    && {
      gap: var(--cds-spacing-100);
    }
  `,

  alignCta: css`
    margin-top: var(--cds-spacing-200);
  `,

  ghostAlignment: css`
    margin-left: calc(var(--cds-spacing-200) * -1);
  `,

  collectionsWrapper: css`
    width: 100%;
  `,

  collectionWrapperVertical: css`
    margin-top: calc(-1 * var(--cds-spacing-300));

    ${breakpoints.down('xs')} {
      margin-top: 0;
    }
  `,

  collections: css`
    margin-top: calc(-1 * var(--cds-spacing-300));
    margin-bottom: calc(-1 * var(--cds-spacing-300));

    .cds-ProductCard-grid {
      min-width: unset;
    }
  `,

  button: css`
    &:hover,
    &:active,
    &:visited {
      text-decoration: none;
    }
  `,

  chip: css`
    &.cds-Chip-root {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-white-0);
      border-color: var(--cds-color-neutral-disabled-strong);
    }

    &.cds-Chip-selected {
      color: var(--cds-color-blue-700);
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
      border-color: var(--cds-color-blue-700);
    }

    &.cds-Chip-root:hover {
      color: var(--cds-color-neutral-primary);
      background-color: var(--cds-color-neutral-disabled-weak);
      border-color: var(--cds-color-neutral-primary);
    }
  `,

  imageFilter: css`
    padding: var(--cds-spacing-100);
  `,
};

function CollectionsModule(props: Props) {
  const {
    filters,
    layout,
    showAllEntries,
    onFilterChange,
    backgroundColor,
    cta,
    description,
    headline,
    collections = [],
    isCollectionsLoading,
    currentCollectionSetIndex = 0,
    numOfEntriesPerCollection = 0,
    customCtaButton,
    customChipStyles,
    forceOpenInNewTab,
    productCardTrackingName,
    eventingData,
  } = props;
  const layoutConfig = getModuleLayoutConfig(props);
  const isTabletOrSmaller = useMediaQuery(breakpoints.down('sm'));
  const isMobile = useMediaQuery(breakpoints.down('xs'));
  const isSearchMerchandisingCollection = getShouldShowGoogleAISpotlightCollectionsInSearch();
  if (isMobile || (isTabletOrSmaller && numOfEntriesPerCollection === MAX_NUMBER_OF_SPOTLIGHT_COLLECTION_PRODUCTS)) {
    layoutConfig.layout = MODULE_LAYOUTS.textTopImageBottom;
  }
  // show one less entry if there is an odd number of entries, to prevent an orhpaned card on the last row
  const numOfEntriesPerCollectionToDisplay = numOfEntriesPerCollection;

  const isHorizontal = isHorizontalLayout({ layout });

  const itemsGridConfig: GridConfig =
    numOfEntriesPerCollection === 2 ? { lg: 6, md: 6, sm: 6, xs: 12 } : { lg: 12, md: 12, sm: 12, xs: 12 };

  const collectionsToDisplay = numOfEntriesPerCollectionToDisplay
    ? collections.map((collection) => ({
        ...collection,
        entities: collection.entities,
      }))
    : collections;

  const isInverted = isInvertedColor(backgroundColor ?? '');
  let variantOfCta: React.ComponentProps<typeof ButtonType>['variant'] = cta?.variant ?? 'secondary';
  if (cta?.variant === 'ghost') {
    variantOfCta = isInverted ? 'ghostInvert' : 'ghost';
  }

  let button: React.ReactNode;

  if (customCtaButton) {
    button = customCtaButton;
  } else {
    button =
      cta?.text && cta?.target ? (
        <div css={[styles.alignCta, cta.variant === 'ghost' ? styles.ghostAlignment : undefined]}>
          <Button
            component="a"
            variant={variantOfCta}
            icon={cta.showCtaArrowIcon || cta.variant === 'ghost' ? <ArrowNextIcon /> : undefined}
            href={cta.target}
            target={forceOpenInNewTab ? '_blank' : undefined}
            onClick={cta.onClick}
            css={cta.variant === 'ghost' ? null : styles.button}
          >
            {cta.text}
          </Button>
        </div>
      ) : null;
  }

  // only show image filters if all filters have images
  const shouldRenderImagesForFilters = !filters?.some((filter) => !filter.filterImage);
  const renderImage = () => {
    return (
      <div css={[styles.collectionsWrapper, isHorizontal ? undefined : styles.collectionWrapperVertical]}>
        {filters && filters.length > 1 && (
          <ChipGroup aria-label={_t('Filter By')} css={styles.filters}>
            {filters &&
              filters.map(({ filterName, id, filterImage }, idx) => (
                <TrackedChip
                  key={id}
                  selected={currentCollectionSetIndex === idx}
                  onClick={() => onFilterChange?.(idx, filterName)}
                  css={shouldRenderImagesForFilters ? styles.chip : customChipStyles}
                  aria-label={filterName}
                  trackingName="collections_filter_chip"
                >
                  {/* @ts-expect-error */}
                  {shouldRenderImagesForFilters ? (
                    <div css={styles.imageFilter}>
                      <Image {...filterImage} description="" height={28} />
                    </div>
                  ) : (
                    filterName ?? ''
                  )}
                </TrackedChip>
              ))}
          </ChipGroup>
        )}
        <div css={styles.collections}>
          <CollectionsContainer
            productCardTrackingName={productCardTrackingName}
            collections={collectionsToDisplay}
            isCollectionsLoading={isCollectionsLoading}
            itemsGridConfig={itemsGridConfig}
            hideLoadingTitle
            showAllEntries={showAllEntries ?? false}
            renderTitle={() => null}
            isSearchMerchandisingCollection={isSearchMerchandisingCollection}
            eventingData={eventingData}
          />
        </div>
        <Hidden {...(numOfEntriesPerCollection < 2 ? { smUp: true } : { mdUp: true })}>{button}</Hidden>
      </div>
    );
  };

  const renderContent = () => {
    if (isCollectionsLoading) {
      return (
        <div>
          <Skeleton width="70%" css={styles.heading} height={40} />
          <Skeleton width="100%" css={styles.description} />
          <Skeleton width="100%" css={styles.description} />
          <Skeleton width="100%" css={styles.description} />
        </div>
      );
    }
    return (
      <div>
        <Typography2 variant="titleSmall" component="h2" color="inherit" css={styles.heading}>
          {headline}
        </Typography2>
        {description && (
          <Typography2 variant="bodyPrimary" component="p" color="inherit" css={styles.description}>
            {description}
          </Typography2>
        )}
        <Hidden {...(numOfEntriesPerCollection < 2 ? { xsDown: true } : { smDown: true })}>{button}</Hidden>
      </div>
    );
  };

  return <ModuleLayout renderContent={renderContent} renderImage={renderImage} assembly={layoutConfig} />;
}

export default CollectionsModule;
